/**
 * Configuration Store - Global state management
 * Uses Zustand for simple, type-safe state management
 */

import { create } from 'zustand';
import { SCALE_MASKS } from '@/types/config';
import type { NoteMapping, LEDSettings, LEDDefaultBehavior, ScaleColoringConfig, ScaleType, KeyOverride, LEDOverride, Layer, Zone, ZoneBounds, LEDLayerConfig, AnalogConfig, TuningTable } from '@/types/config';
import type { HexCoord } from '@/types/hex';
import type { KeyAction } from '@/types/midi';
import type { NoteDisplayMode } from '@/lib/noteDisplay';
import { presetMappings } from '@/lib/noteMapping';

/**
 * Convert HSL (h: 0-360, s: 0-1, l: 0-1) to RGB [0-255, 0-255, 0-255].
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60)       { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else              { r = c; b = x; }
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/**
 * Compute the minimum pitch bend range (semitones) needed for a tuning table.
 * Finds the max deviation of any pitch from its nearest 12-TET semitone.
 */
function computeOptimalPitchBendRange(table: TuningTable): number {
  let maxDeviation = 0; // 0.1-cent units

  if (table.period_is_octave) {
    // Octave tuning: deviations repeat exactly, check one period
    for (let d = 0; d < table.notes_per_period; d++) {
      const pitch = table.pitches[d] ?? 0;
      const nearest = Math.round(pitch / 1000) * 1000;
      maxDeviation = Math.max(maxDeviation, Math.abs(pitch - nearest));
    }
  } else {
    // Non-octave: period drift accumulates, check ±20 periods
    for (let p = -20; p <= 20; p++) {
      for (let d = 0; d < table.notes_per_period; d++) {
        const pitch = p * table.period_cents + (table.pitches[d] ?? 0);
        const nearest = Math.round(pitch / 1000) * 1000;
        maxDeviation = Math.max(maxDeviation, Math.abs(pitch - nearest));
      }
    }
  }

  // Default 48 to match common synth defaults (e.g. Surge XT), increase only if needed
  return Math.max(48, Math.min(96, Math.ceil(maxDeviation / 1000)));
}

/** Data-only fields from ConfigStore (excludes action functions) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ConfigStoreData = { [K in keyof ConfigStore as ConfigStore[K] extends (...args: any[]) => any ? never : K]: ConfigStore[K] };

export interface ConfigStore {
  // Grid configuration (spec §5.2)
  columns: number; // 1-8
  rows: number; // 1-4
  keysPerModule: 12 | 16; // Keys per module (12 for 3x4, 16 for 4x4)

  // Note mapping (spec §5.3) - global default
  noteMapping: NoteMapping;

  // Layers (spec §5.5) - each can have optional note_mapping
  layers: Layer[];

  // LED settings (spec §5.10)
  ledSettings: LEDSettings;

  // LED default behavior (spec §5.11)
  ledDefaultBehavior: LEDDefaultBehavior;

  // Scale coloring (spec §5.14)
  scaleColoring: ScaleColoringConfig;

  // Key overrides (spec §5.7)
  overrides: KeyOverride[];

  // LED overrides (spec §5.12)
  ledOverrides: LEDOverride[];

  // Zones (spec §5.6)
  zones: Zone[];

  // LED layers (spec §5.13)
  ledLayers: LEDLayerConfig[];

  // Analog inputs configuration (spec §5.18, v1.4, optional)
  analogConfig?: AnalogConfig;

  // User-friendly config name (v2.1+, optional, max 31 chars + null)
  // Survives BRI round-trip via header flag + 32-byte name section.
  configName?: string;

  // Coordinate stagger: additional offset per axis step
  stagger: { q: number; r: number }; // q = q-shift per r-step, r = r-shift per q-step

  // Module row shift: additional q-offset per module row (on top of fixed -2 compensation)
  moduleRowShift: number;

  // Microtonal tuning (v2.0)
  tuningEnabled: boolean;
  tuningTable?: TuningTable;
  tuningBaseFrequency: number; // Reference frequency at origin key (Hz), default 440
  noteDisplayMode: NoteDisplayMode;

  // Power budget (webapp-only, not serialized to device)
  powerBudget: {
    converterAmps: number;
    maxSimultaneousPressed: number;
  };

  // Live key visualization
  liveVisualizationEnabled: boolean;

  // Selected key for editing (UI state)
  selectedKey: HexCoord | null;
  activeLayer: number; // Currently active layer (for overrides)

  // Zone editing preview (transient UI state)
  zonePreview: ZoneBounds | null;

  // Refresh trigger (forces color recalculation)
  refreshTrigger: number;

  // Actions - Grid
  setColumns: (columns: number) => void;
  setRows: (rows: number) => void;
  setKeysPerModule: (kpm: 12 | 16) => void;
  setStagger: (q: number, r: number) => void;
  setModuleRowShift: (shift: number) => void;

  // Actions - Note Mapping (global default)
  setNoteMapping: (mapping: NoteMapping) => void;
  updateNoteMappingOrigin: (q: number, r: number) => void;
  updateNoteMappingBase: (baseNote: number) => void;
  updateNoteMappingIntervals: (q: number, r: number) => void;

  // Actions - Layers (spec §5.5)
  addLayer: (name: string) => void;
  removeLayer: (layerId: number) => void;
  updateLayerName: (layerId: number, name: string) => void;
  setLayerNoteMapping: (layerId: number, mapping: NoteMapping | null) => void;

  // Actions - LED Settings
  setLEDEnabled: (enabled: boolean) => void;
  setLEDBrightness: (brightness: number) => void;
  setLEDUpdateRate: (rate: number) => void;
  setLEDFadeTime: (fadeMs: number) => void;
  setLEDPerceptualProfile: (profile: 'default' | 'soft' | 'vivid') => void;

  // Actions - LED Default Colors (spec §5.11)
  setLEDIdleColor: (color: [number, number, number]) => void;
  setLEDIdleBrightness: (brightness: number) => void;
  setLEDPressedColor: (color: [number, number, number]) => void;
  setLEDPressedBrightness: (brightness: number) => void;

  // Actions - Scale Coloring (spec §5.14)
  setScaleColoringEnabled: (enabled: boolean) => void;
  setScaleColoringRootNote: (rootNote: number) => void;
  setScaleType: (scaleType: ScaleType) => void;
  setScaleColoringRootColor: (color: [number, number, number]) => void;
  setScaleColoringInScaleColor: (color: [number, number, number]) => void;
  setScaleColoringNonScaleColor: (color: [number, number, number]) => void;

  // Actions - Key Selection
  setSelectedKey: (key: HexCoord | null) => void;
  setActiveLayer: (layer: number) => void;

  // Actions - Key Overrides (spec §5.7)
  addOverride: (pos: HexCoord, layer: number, action: KeyAction) => void;
  updateOverride: (pos: HexCoord, layer: number, action: KeyAction) => void;
  removeOverride: (pos: HexCoord, layer: number) => void;

  // Actions - LED Overrides (spec §5.12)
  addLEDOverride: (override: LEDOverride) => void;
  updateLEDOverride: (pos: HexCoord, layer: number, idle: LEDOverride['idle'], pressed?: LEDOverride['pressed']) => void;
  removeLEDOverride: (pos: HexCoord, layer: number) => void;

  // Actions - Zones (spec §5.6)
  addZone: (zone: Zone) => void;
  updateZone: (index: number, zone: Zone) => void;
  removeZone: (index: number) => void;
  reorderZones: (fromIndex: number, toIndex: number) => void;
  setZonePreview: (bounds: ZoneBounds | null) => void;

  // Actions - LED Layers (spec §5.13)
  addLEDLayer: (config: LEDLayerConfig) => void;
  updateLEDLayer: (layerId: number, config: Partial<Omit<LEDLayerConfig, 'layer'>>) => void;
  removeLEDLayer: (layerId: number) => void;

  // Actions - Scale Coloring Per-Degree (v2.0)
  setScaleColoringMode: (mode: 'three_color' | 'per_degree') => void;
  setDegreeColor: (degree: number, color: [number, number, number]) => void;
  initDegreeColors: () => void;
  initSpectrumColors: (interval: number, fillAll?: boolean, cycleShift?: number) => void;

  // Actions - Microtonal Tuning (v2.0)
  setTuningEnabled: (enabled: boolean) => void;
  setTuningTable: (table: TuningTable | undefined) => void;
  setEdoTuning: (divisions: number) => void;
  setScaleColoringRootDegree: (degree: number) => void;
  setPitchBendRange: (range: number) => void;
  setNoteDisplayMode: (mode: NoteDisplayMode) => void;
  loadTuning: (table: TuningTable, suggestedIntervals?: { q_cents: number; r_cents: number }) => void;
  setPitchValue: (degree: number, centsX10: number) => void;
  setTuningBaseFrequency: (freq: number) => void;
  setIntervalCents: (axis: 'q' | 'r', centsX10: number) => void;

  // Actions - Analog Inputs (spec §5.18, v1.4)
  setAnalogConfig: (config: AnalogConfig | undefined) => void;
  updateSliderConfig: (index: number, config: Partial<AnalogConfig['sliders'][0]>) => void;
  updatePedalConfig: (config: Partial<AnalogConfig['pedal']>) => void;

  // Actions - Power Budget
  setConverterAmps: (amps: number) => void;
  setMaxSimultaneousPressed: (count: number) => void;

  // Actions - Live visualization
  setLiveVisualizationEnabled: (enabled: boolean) => void;

  // Actions - Refresh
  refreshColors: () => void;

  // Actions - Import/Export
  loadConfiguration: (config: Partial<ConfigStoreData>) => void;
}

/**
 * Global configuration store
 * Spec §5: Configuration structure
 */
export const useConfigStore = create<ConfigStore>((set) => ({
  // Initial values (spec defaults)
  columns: 2, // Start with 2×2 grid (48 keys)
  rows: 2,
  keysPerModule: 16, // Default: 16-key (4x4) modules

  // Wicki-Hayden as default: horizontal = whole tones, vertical = octaves
  // (spec §5.3, SMC 2011: V=+12 octave, H=+2 whole tone)
  noteMapping: presetMappings.wickiHayden,

  // Default layer (spec §5.5: minimum 1 layer)
  layers: [
    { id: 0, name: 'default', default: true },
  ],

  // LED settings defaults (spec §5.10)
  ledSettings: {
    enabled: true,
    brightness: 0.5, // Spec §5.10: default global brightness
    perceptual_profile: 'default', // Optional, default profile
    update_rate_hz: 120, // Spec §5.10: 30-200 Hz range
    fade_default_ms: 150, // 0-2000ms range (smooth default)
  },

  // LED default behavior (spec §5.11)
  ledDefaultBehavior: {
    idle: {
      // Spec §5.11 example uses [30,30,30] but too dark for visualization
      // Using brighter base color so global brightness is actually visible
      color: [150, 150, 150], // Medium gray base
      brightness: 0.5, // 50% local brightness (adjustable for visibility)
    },
    pressed: {
      color: [255, 255, 255], // Spec §5.11: white when pressed
      brightness: 1.0,
      fade_ms: 0,
    },
  },

  // Scale coloring (spec §5.14)
  scaleColoring: {
    enabled: true, // Enabled by default
    root_note: 60, // C4 (middle C)
    scale_type: 'major' as ScaleType,
    colors: {
      root: [255, 0, 0], // Red for root notes
      in_scale: [0, 100, 255], // Blue for in-scale notes
      non_scale: [80, 80, 80], // Gray for non-scale notes
    },
  },

  // Key overrides (spec §5.7) - empty by default
  overrides: [],

  // LED overrides (spec §5.12) - empty by default
  ledOverrides: [],

  // Zones (spec §5.6) - empty by default
  zones: [],

  // LED layers (spec §5.13) - empty by default
  ledLayers: [],

  // Coordinate stagger: {q: q-shift per r-step, r: r-shift per q-step}
  stagger: { q: 0, r: 0 },

  // Module row shift: additional q-offset per module row (on top of fixed -2 compensation)
  moduleRowShift: 0,

  // Power budget (webapp-only)
  powerBudget: {
    converterAmps: 3.0,
    maxSimultaneousPressed: 10,
  },

  // Live key visualization
  liveVisualizationEnabled: (() => {
    try {
      return localStorage.getItem('stak-live-viz') !== '0';
    } catch { return true; }
  })(),

  // Microtonal tuning (v2.0) - disabled by default
  tuningEnabled: false,
  tuningBaseFrequency: 440,
  noteDisplayMode: 'note_number' as NoteDisplayMode,

  // Selected key (UI state)
  selectedKey: null,
  activeLayer: 0,
  zonePreview: null,

  // Refresh trigger (incremented to force color updates)
  refreshTrigger: 0,

  // Grid size actions (spec §5.2: 1-8 columns, 1-4 rows)
  setColumns: (columns) =>
    set({ columns: Math.max(1, Math.min(8, columns)) }),

  setRows: (rows) => set({ rows: Math.max(1, Math.min(4, rows)) }),

  setKeysPerModule: (kpm) => set({ keysPerModule: kpm }),

  setStagger: (q, r) =>
    set((state) => ({
      stagger: {
        q: Math.max(-4, Math.min(4, Math.round(q))),
        r: Math.max(-4, Math.min(4, Math.round(r))),
      },
      refreshTrigger: state.refreshTrigger + 1,
    })),

  setModuleRowShift: (shift) =>
    set((state) => ({
      moduleRowShift: Math.max(-4, Math.min(4, Math.round(shift))),
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // Note mapping actions (spec §5.3)
  setNoteMapping: (mapping) => set({ noteMapping: mapping }),

  updateNoteMappingOrigin: (q, r) =>
    set((state) => ({
      noteMapping: {
        ...state.noteMapping,
        origin: { q, r },
      },
    })),

  updateNoteMappingBase: (baseNote) =>
    set((state) => ({
      noteMapping: {
        ...state.noteMapping,
        base_note: Math.max(0, Math.min(127, baseNote)),
      },
    })),

  updateNoteMappingIntervals: (q, r) =>
    set((state) => ({
      noteMapping: {
        ...state.noteMapping,
        intervals: {
          q: Math.max(-127, Math.min(127, q)),
          r: Math.max(-127, Math.min(127, r)),
        },
      },
    })),

  // Layer actions (spec §5.5)
  addLayer: (name) =>
    set((state) => {
      // Find next available ID (spec §5.5: layer ID 0-15)
      const usedIds = new Set(state.layers.map((l) => l.id));
      let newId = 0;
      while (usedIds.has(newId) && newId < 16) newId++;
      if (newId >= 16) return state; // Max 16 layers
      return {
        layers: [...state.layers, { id: newId, name: name.slice(0, 32) }],
      };
    }),

  removeLayer: (layerId) =>
    set((state) => {
      // Cannot remove default layer
      const layer = state.layers.find((l) => l.id === layerId);
      if (!layer || layer.default) return state;
      return {
        layers: state.layers.filter((l) => l.id !== layerId),
        // Reset activeLayer if removed
        activeLayer: state.activeLayer === layerId ? 0 : state.activeLayer,
      };
    }),

  updateLayerName: (layerId, name) =>
    set((state) => ({
      layers: state.layers.map((l) =>
        l.id === layerId ? { ...l, name: name.slice(0, 32) } : l
      ),
    })),

  setLayerNoteMapping: (layerId, mapping) =>
    set((state) => ({
      layers: state.layers.map((l): Layer => {
        if (l.id !== layerId) return l;
        if (mapping) {
          return { ...l, note_mapping: mapping };
        }
        // Remove note_mapping - construct clean object
        const { note_mapping: _, ...rest } = l;
        return rest;
      }),
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // LED settings actions (spec §5.10)
  setLEDEnabled: (enabled) =>
    set((state) => ({
      ledSettings: { ...state.ledSettings, enabled },
    })),

  setLEDBrightness: (brightness) =>
    set((state) => ({
      ledSettings: {
        ...state.ledSettings,
        // Spec §5.10: brightness ∈ [0.0, 1.0]
        brightness: Math.max(0, Math.min(1, brightness)),
      },
    })),

  setLEDUpdateRate: (rate) =>
    set((state) => ({
      ledSettings: {
        ...state.ledSettings,
        // Spec §5.10: update_rate_hz ∈ [30, 200]
        update_rate_hz: Math.max(30, Math.min(200, Math.round(rate))),
      },
    })),

  setLEDFadeTime: (fadeMs) =>
    set((state) => ({
      ledSettings: {
        ...state.ledSettings,
        // fade_default_ms ∈ [0, 2000]
        fade_default_ms: Math.max(0, Math.min(2000, Math.round(fadeMs))),
      },
    })),

  setLEDPerceptualProfile: (profile) =>
    set((state) => ({
      ledSettings: {
        ...state.ledSettings,
        // Spec §5.10: perceptual_profile ∈ ["default", "soft", "vivid"]
        perceptual_profile: profile,
      },
    })),

  // LED default color actions (spec §5.11)
  setLEDIdleColor: (color) =>
    set((state) => ({
      ledDefaultBehavior: {
        ...state.ledDefaultBehavior,
        idle: {
          ...state.ledDefaultBehavior.idle,
          // Spec §5.11: RGB color [0-255, 0-255, 0-255]
          color: [
            Math.max(0, Math.min(255, Math.round(color[0]))),
            Math.max(0, Math.min(255, Math.round(color[1]))),
            Math.max(0, Math.min(255, Math.round(color[2]))),
          ],
        },
      },
    })),

  setLEDIdleBrightness: (brightness) =>
    set((state) => ({
      ledDefaultBehavior: {
        ...state.ledDefaultBehavior,
        idle: {
          ...state.ledDefaultBehavior.idle,
          // Spec §5.11: brightness ∈ [0.0, 1.0]
          brightness: Math.max(0, Math.min(1, brightness)),
        },
      },
    })),

  setLEDPressedColor: (color) =>
    set((state) => ({
      ledDefaultBehavior: {
        ...state.ledDefaultBehavior,
        pressed: {
          ...state.ledDefaultBehavior.pressed,
          // Spec §5.11: RGB color [0-255, 0-255, 0-255]
          color: [
            Math.max(0, Math.min(255, Math.round(color[0]))),
            Math.max(0, Math.min(255, Math.round(color[1]))),
            Math.max(0, Math.min(255, Math.round(color[2]))),
          ],
        },
      },
    })),

  setLEDPressedBrightness: (brightness) =>
    set((state) => ({
      ledDefaultBehavior: {
        ...state.ledDefaultBehavior,
        pressed: {
          ...state.ledDefaultBehavior.pressed,
          // Spec §5.11: brightness ∈ [0.0, 1.0]
          brightness: Math.max(0, Math.min(1, brightness)),
        },
      },
    })),

  // Scale coloring actions (spec §5.14)
  setScaleColoringEnabled: (enabled) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        enabled,
      },
      // When enabling scale coloring, disable all LED layer modes
      ...(enabled && {
        ledLayers: state.ledLayers.length > 0
          // Set all existing layer configs to 'off'
          ? state.ledLayers.map((l) => ({ ...l, mode: 'off' as const }))
          // If no layer configs exist, create one for default layer 0 with mode 'off'
          : [{ layer: 0, base_color: [80, 80, 80] as [number, number, number], brightness: 0.5, mode: 'off' as const }],
      }),
    })),

  setScaleColoringRootNote: (rootNote) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        // Spec §5.14: root_note ∈ [0, 127]
        root_note: Math.max(0, Math.min(127, rootNote)),
      },
    })),

  setScaleType: (scaleType) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        scale_type: scaleType,
      },
    })),

  setScaleColoringRootColor: (color) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        colors: {
          ...state.scaleColoring.colors,
          root: [
            Math.max(0, Math.min(255, Math.round(color[0]))),
            Math.max(0, Math.min(255, Math.round(color[1]))),
            Math.max(0, Math.min(255, Math.round(color[2]))),
          ],
        },
      },
    })),

  setScaleColoringInScaleColor: (color) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        colors: {
          ...state.scaleColoring.colors,
          in_scale: [
            Math.max(0, Math.min(255, Math.round(color[0]))),
            Math.max(0, Math.min(255, Math.round(color[1]))),
            Math.max(0, Math.min(255, Math.round(color[2]))),
          ],
        },
      },
    })),

  setScaleColoringNonScaleColor: (color) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        colors: {
          ...state.scaleColoring.colors,
          non_scale: [
            Math.max(0, Math.min(255, Math.round(color[0]))),
            Math.max(0, Math.min(255, Math.round(color[1]))),
            Math.max(0, Math.min(255, Math.round(color[2]))),
          ],
        },
      },
    })),

  // Key selection actions
  setSelectedKey: (key) => set({ selectedKey: key }),

  setActiveLayer: (layer) =>
    set({ activeLayer: Math.max(0, Math.min(15, layer)) }),

  // Key override actions (spec §5.7)
  addOverride: (pos, layer, action) =>
    set((state) => {
      // Layer actions are cross-layer: always store on layer 0
      const effectiveLayer = (action.type === 'layer_toggle' || action.type === 'layer_momentary')
        ? 0 : layer;
      // Check if override already exists
      const existingIndex = state.overrides.findIndex(
        (o) => o.pos.q === pos.q && o.pos.r === pos.r && o.layer === effectiveLayer
      );
      if (existingIndex !== -1) {
        // Update existing
        const newOverrides = [...state.overrides];
        newOverrides[existingIndex] = { pos, layer: effectiveLayer, action };
        return { overrides: newOverrides, refreshTrigger: state.refreshTrigger + 1 };
      }
      // Add new
      return {
        overrides: [...state.overrides, { pos, layer: effectiveLayer, action }],
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  updateOverride: (pos, layer, action) =>
    set((state) => {
      // Layer actions are cross-layer: always store on layer 0
      const effectiveLayer = (action.type === 'layer_toggle' || action.type === 'layer_momentary')
        ? 0 : layer;
      const index = state.overrides.findIndex(
        (o) => o.pos.q === pos.q && o.pos.r === pos.r && o.layer === effectiveLayer
      );
      if (index === -1) return state;
      const newOverrides = [...state.overrides];
      newOverrides[index] = { pos, layer: effectiveLayer, action };
      return { overrides: newOverrides, refreshTrigger: state.refreshTrigger + 1 };
    }),

  removeOverride: (pos, layer) =>
    set((state) => ({
      overrides: state.overrides.filter(
        (o) => !(o.pos.q === pos.q && o.pos.r === pos.r && o.layer === layer)
      ),
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // LED override actions (spec §5.12)
  addLEDOverride: (override) =>
    set((state) => {
      // Check if LED override already exists
      const existingIndex = state.ledOverrides.findIndex(
        (o) => o.pos.q === override.pos.q && o.pos.r === override.pos.r && o.layer === override.layer
      );
      if (existingIndex !== -1) {
        // Update existing
        const newOverrides = [...state.ledOverrides];
        newOverrides[existingIndex] = override;
        return { ledOverrides: newOverrides, refreshTrigger: state.refreshTrigger + 1 };
      }
      // Add new
      return {
        ledOverrides: [...state.ledOverrides, override],
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  updateLEDOverride: (pos, layer, idle, pressed) =>
    set((state) => {
      const index = state.ledOverrides.findIndex(
        (o) => o.pos.q === pos.q && o.pos.r === pos.r && o.layer === layer
      );
      if (index === -1) return state;
      const newOverrides = [...state.ledOverrides];
      newOverrides[index] = { pos, layer, idle, ...(pressed && { pressed }) };
      return { ledOverrides: newOverrides, refreshTrigger: state.refreshTrigger + 1 };
    }),

  removeLEDOverride: (pos, layer) =>
    set((state) => ({
      ledOverrides: state.ledOverrides.filter(
        (o) => !(o.pos.q === pos.q && o.pos.r === pos.r && o.layer === layer)
      ),
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // Zone actions (spec §5.6)
  addZone: (zone) =>
    set((state) => ({
      zones: [...state.zones, {
        ...zone,
        name: zone.name.slice(0, 32), // Spec §5.6: max 32 chars
        channel: Math.max(0, Math.min(15, zone.channel)), // 0-15
        transpose: Math.max(-24, Math.min(24, zone.transpose)), // -24 to +24
      }],
      refreshTrigger: state.refreshTrigger + 1,
    })),

  updateZone: (index, zone) =>
    set((state) => {
      if (index < 0 || index >= state.zones.length) return state;
      const newZones = [...state.zones];
      newZones[index] = {
        ...zone,
        name: zone.name.slice(0, 32),
        channel: Math.max(0, Math.min(15, zone.channel)),
        transpose: Math.max(-24, Math.min(24, zone.transpose)),
      };
      return { zones: newZones, refreshTrigger: state.refreshTrigger + 1 };
    }),

  removeZone: (index) =>
    set((state) => {
      if (index < 0 || index >= state.zones.length) return state;
      return {
        zones: state.zones.filter((_, i) => i !== index),
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  reorderZones: (fromIndex, toIndex) =>
    set((state) => {
      if (fromIndex < 0 || fromIndex >= state.zones.length) return state;
      if (toIndex < 0 || toIndex >= state.zones.length) return state;
      if (fromIndex === toIndex) return state;

      const newZones = [...state.zones];
      const removed = newZones.splice(fromIndex, 1)[0];
      if (!removed) return state; // Type guard - should never happen given bounds checks
      newZones.splice(toIndex, 0, removed);
      return { zones: newZones, refreshTrigger: state.refreshTrigger + 1 };
    }),

  setZonePreview: (bounds) => set({ zonePreview: bounds }),

  // LED Layer actions (spec §5.13)
  addLEDLayer: (config) =>
    set((state) => {
      // Check if layer already has LED config
      const existing = state.ledLayers.find((l) => l.layer === config.layer);
      if (existing) {
        // Update existing
        return {
          ledLayers: state.ledLayers.map((l) =>
            l.layer === config.layer ? { ...config, brightness: Math.max(0, Math.min(1, config.brightness)) } : l
          ),
          refreshTrigger: state.refreshTrigger + 1,
        };
      }
      // Add new
      return {
        ledLayers: [...state.ledLayers, {
          ...config,
          brightness: Math.max(0, Math.min(1, config.brightness)),
        }],
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  updateLEDLayer: (layerId, config) =>
    set((state) => {
      const index = state.ledLayers.findIndex((l) => l.layer === layerId);
      if (index === -1) return state;
      const existing = state.ledLayers[index];
      if (!existing) return state; // Type guard
      const newLayers = [...state.ledLayers];
      newLayers[index] = {
        layer: existing.layer,
        base_color: config.base_color ?? existing.base_color,
        brightness: config.brightness !== undefined
          ? Math.max(0, Math.min(1, config.brightness))
          : existing.brightness,
        mode: config.mode ?? existing.mode,
      };
      return { ledLayers: newLayers, refreshTrigger: state.refreshTrigger + 1 };
    }),

  removeLEDLayer: (layerId) =>
    set((state) => ({
      ledLayers: state.ledLayers.filter((l) => l.layer !== layerId),
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // Scale coloring per-degree actions (v2.0)
  setScaleColoringMode: (mode) =>
    set((state) => {
      const N = state.tuningTable?.notes_per_period ?? 12;
      if (mode === 'per_degree' && (!state.scaleColoring.degree_colors || state.scaleColoring.degree_colors.length !== N)) {
        // Auto-initialize from three-color scheme
        const rootDegree = state.scaleColoring.root_degree ?? 0;
        const scaleMask = state.tuningTable?.scale_mask
          ?? (SCALE_MASKS[state.scaleColoring.scale_type] ?? SCALE_MASKS.major);
        const degreeColors: [number, number, number][] = Array.from({ length: N }, (_, i) => {
          if (i === rootDegree) return [...state.scaleColoring.colors.root] as [number, number, number];
          if (scaleMask[i]) return [...state.scaleColoring.colors.in_scale] as [number, number, number];
          return [...state.scaleColoring.colors.non_scale] as [number, number, number];
        });
        return {
          scaleColoring: { ...state.scaleColoring, color_mode: mode, degree_colors: degreeColors },
          refreshTrigger: state.refreshTrigger + 1,
        };
      }
      return {
        scaleColoring: { ...state.scaleColoring, color_mode: mode },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  setDegreeColor: (degree, color) =>
    set((state) => {
      const colors = state.scaleColoring.degree_colors ? [...state.scaleColoring.degree_colors] : [];
      if (degree < 0 || degree >= colors.length) return state;
      colors[degree] = [
        Math.max(0, Math.min(255, Math.round(color[0]))),
        Math.max(0, Math.min(255, Math.round(color[1]))),
        Math.max(0, Math.min(255, Math.round(color[2]))),
      ];
      return {
        scaleColoring: { ...state.scaleColoring, degree_colors: colors },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  initDegreeColors: () =>
    set((state) => {
      const N = state.tuningTable?.notes_per_period ?? 12;
      const rootDegree = state.scaleColoring.root_degree ?? 0;
      const scaleMask = state.tuningTable?.scale_mask
        ?? (SCALE_MASKS[state.scaleColoring.scale_type] ?? SCALE_MASKS.major);
      const degreeColors: [number, number, number][] = Array.from({ length: N }, (_, i) => {
        if (i === rootDegree) return [...state.scaleColoring.colors.root] as [number, number, number];
        if (scaleMask[i]) return [...state.scaleColoring.colors.in_scale] as [number, number, number];
        return [...state.scaleColoring.colors.non_scale] as [number, number, number];
      });
      return {
        scaleColoring: { ...state.scaleColoring, degree_colors: degreeColors },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  initSpectrumColors: (interval, fillAll, cycleShift) =>
    set((state) => {
      const N = state.tuningTable?.notes_per_period ?? 12;
      const clampedInterval = Math.max(1, Math.min(N - 1, interval));
      const visited: number[] = [];
      const visitedSet = new Set<number>();
      const usedStarts = new Set<number>();
      let start = 0;
      // Walk degrees by interval in one or more cycles
      while (true) {
        if (usedStarts.has(start)) break;
        usedStarts.add(start);
        let deg = start;
        do {
          if (!visitedSet.has(deg)) {
            visited.push(deg);
            visitedSet.add(deg);
          }
          deg = (deg + clampedInterval) % N;
        } while (deg !== start);
        if (visitedSet.size >= N || !fillAll) break;
        const clampedShift = Math.max(1, Math.min(N - 1, cycleShift ?? 1));
        start = (start + clampedShift) % N;
      }
      // Assign evenly-spaced hues to visited degrees, gray to unvisited
      const gray: [number, number, number] = [80, 80, 80];
      const degreeColors: [number, number, number][] = Array.from({ length: N }, () => [...gray] as [number, number, number]);
      for (let i = 0; i < visited.length; i++) {
        const hue = (360 * i) / visited.length;
        const d = visited[i]!;
        degreeColors[d] = hslToRgb(hue, 1, 0.5);
      }
      return {
        scaleColoring: { ...state.scaleColoring, color_mode: 'per_degree' as const, degree_colors: degreeColors },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  // Microtonal tuning actions (v2.0)
  setTuningEnabled: (enabled) =>
    set((state) => {
      if (!enabled) {
        // Strip v2.0 fields from noteMapping
        const { cents_mode: _, interval_q_cents: _q, interval_r_cents: _r, pitch_bend_range: _p, ...baseMapping } = state.noteMapping;
        // Reset display mode if currently on a microtonal-only mode
        const microtonalModes: NoteDisplayMode[] = ['edo_step', 'note_cents', 'frequency'];
        const resetMode = microtonalModes.includes(state.noteDisplayMode)
          ? 'note_number' as NoteDisplayMode : state.noteDisplayMode;
        // Remove tuningTable via destructure (can't set undefined with exactOptionalPropertyTypes)
        const { tuningTable: _t, ...withoutTuning } = state;
        void _t;
        return {
          ...withoutTuning,
          tuningEnabled: false,
          noteDisplayMode: resetMode,
          noteMapping: baseMapping as typeof state.noteMapping,
          refreshTrigger: state.refreshTrigger + 1,
        };
      }
      const table: TuningTable = {
        notes_per_period: 12,
        period_is_octave: true,
        period_cents: 12000,
        pitches: Array.from({ length: 12 }, (_, i) => i * 1000), // 1 semitone = 100 cents = 1000 in 0.1-cent
        scale_mask: Array.from({ length: 12 }, () => true),
      };
      return {
        tuningEnabled: true,
        tuningTable: table,
        tuningBaseFrequency: 440,
        noteDisplayMode: 'edo_step' as NoteDisplayMode,
        noteMapping: {
          ...state.noteMapping,
          base_note: 69, // A4 = 440 Hz, matches Scale Workshop convention
          cents_mode: true,
          interval_q_cents: state.noteMapping.intervals.q * 1000, // semitones → 0.1-cent
          interval_r_cents: state.noteMapping.intervals.r * 1000, // semitones → 0.1-cent
          pitch_bend_range: computeOptimalPitchBendRange(table),
        },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  setTuningTable: (table) =>
    set((state) => {
      if (table === undefined) {
        const { tuningTable: _, ...rest } = state;
        return { ...rest, refreshTrigger: state.refreshTrigger + 1 } as Partial<ConfigStore>;
      }
      return { tuningTable: table, refreshTrigger: state.refreshTrigger + 1 };
    }),

  setEdoTuning: (divisions) =>
    set((state) => {
      if (divisions < 1 || divisions > 128) return state;
      const period_cents = 12000; // octave in 0.1-cent
      const pitches = Array.from({ length: divisions }, (_, i) => Math.round(i * period_cents / divisions));
      const scale_mask = Array.from({ length: divisions }, () => true);
      const table: TuningTable = {
        notes_per_period: divisions,
        period_is_octave: true,
        period_cents,
        pitches,
        scale_mask,
      };
      // Approximate Wicki-Hayden for N-EDO:
      // q-axis ~ whole tone (2/12 of octave), r-axis ~ fifth (7/12 of octave)
      const best_wholetone_steps = Math.round(divisions * 2 / 12);
      const best_fifth_steps = Math.round(divisions * 7 / 12);
      // Auto-generate degree_colors if in per_degree mode
      const rootDegree = 0;
      const degreeColors: [number, number, number][] = Array.from({ length: divisions }, (_, i) => {
        if (i === rootDegree) return [...state.scaleColoring.colors.root] as [number, number, number];
        if (scale_mask[i]) return [...state.scaleColoring.colors.in_scale] as [number, number, number];
        return [...state.scaleColoring.colors.non_scale] as [number, number, number];
      });
      return {
        tuningEnabled: true,
        tuningTable: table,
        noteMapping: {
          ...state.noteMapping,
          cents_mode: true,
          interval_q_cents: Math.round(best_wholetone_steps * period_cents / divisions),
          interval_r_cents: Math.round(best_fifth_steps * period_cents / divisions),
          pitch_bend_range: computeOptimalPitchBendRange(table),
        },
        scaleColoring: { ...state.scaleColoring, root_degree: 0, degree_colors: degreeColors },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  setScaleColoringRootDegree: (degree) =>
    set((state) => ({
      scaleColoring: {
        ...state.scaleColoring,
        root_degree: Math.max(0, Math.min(127, degree)),
      },
    })),

  setPitchBendRange: (range) =>
    set((state) => ({
      noteMapping: {
        ...state.noteMapping,
        pitch_bend_range: Math.max(1, Math.min(96, range)),
      },
    })),

  setNoteDisplayMode: (mode) =>
    set({ noteDisplayMode: mode }),

  loadTuning: (table, suggestedIntervals) =>
    set((state) => {
      // Compute Wicki-Hayden fallback if no suggested intervals
      const q_cents = suggestedIntervals?.q_cents
        ?? Math.round(table.period_cents * 2 / 12);
      const r_cents = suggestedIntervals?.r_cents
        ?? Math.round(table.period_cents * 7 / 12);
      // Auto-generate degree_colors
      const degreeColors: [number, number, number][] = Array.from(
        { length: table.notes_per_period },
        (_, i) => {
          if (i === 0) return [...state.scaleColoring.colors.root] as [number, number, number];
          if (table.scale_mask[i]) return [...state.scaleColoring.colors.in_scale] as [number, number, number];
          return [...state.scaleColoring.colors.non_scale] as [number, number, number];
        }
      );
      return {
        tuningEnabled: true,
        tuningTable: table,
        // Show ratio labels from .scl when available
        ...(table.pitch_labels ? { noteDisplayMode: 'frequency' as NoteDisplayMode } : {}),
        noteMapping: {
          ...state.noteMapping,
          cents_mode: true,
          interval_q_cents: q_cents,
          interval_r_cents: r_cents,
          pitch_bend_range: computeOptimalPitchBendRange(table),
        },
        scaleColoring: {
          ...state.scaleColoring,
          root_degree: 0,
          degree_colors: degreeColors,
        },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  setPitchValue: (degree, centsX10) =>
    set((state) => {
      if (!state.tuningTable) return state;
      if (degree <= 0 || degree >= state.tuningTable.notes_per_period) return state;
      const pitches = [...state.tuningTable.pitches];
      const lower = pitches[degree - 1] ?? 0;
      const upper = degree < pitches.length - 1
        ? (pitches[degree + 1] ?? state.tuningTable.period_cents)
        : state.tuningTable.period_cents;
      pitches[degree] = Math.max(lower + 1, Math.min(upper - 1, centsX10));
      const newTable = { ...state.tuningTable, pitches };
      return {
        tuningTable: newTable,
        noteMapping: { ...state.noteMapping, pitch_bend_range: computeOptimalPitchBendRange(newTable) },
        refreshTrigger: state.refreshTrigger + 1,
      };
    }),

  setTuningBaseFrequency: (freq) =>
    set({ tuningBaseFrequency: Math.max(1, Math.min(20000, freq)) }),

  setIntervalCents: (axis, centsX10) =>
    set((state) => ({
      noteMapping: {
        ...state.noteMapping,
        ...(axis === 'q'
          ? { interval_q_cents: centsX10 }
          : { interval_r_cents: centsX10 }),
      },
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // Analog Inputs actions (spec §5.18, v1.4)
  setAnalogConfig: (config) =>
    set((state) => {
      if (config === undefined) {
        // Remove analogConfig by returning state without it
        const { analogConfig: _, ...rest } = state;
        return rest as ConfigStore;
      }
      return { analogConfig: config };
    }),

  updateSliderConfig: (index, config) =>
    set((state) => {
      if (index < 0 || index > 2) return state;

      // Create default analog config if not present
      const currentConfig: AnalogConfig = state.analogConfig ?? {
        sliders: [
          { midi_type: 'cc', channel: 0, cc: 1, min: 0, max: 127, invert: false, spring_center: false },
          { midi_type: 'cc', channel: 0, cc: 2, min: 0, max: 127, invert: false, spring_center: false },
          { midi_type: 'cc', channel: 0, cc: 3, min: 0, max: 127, invert: false, spring_center: false },
        ],
        pedal: {
          detect_mode: 'auto',
          sustain: { cc: 64, channel: 0, invert: false },
          expression: { cc: 11, channel: 0, min: 0, max: 127, invert: false },
        },
      };

      // Get current slider and merge with updates to create complete config
      // Index is validated to be 0, 1, or 2 above
      const sliderIndex = index as 0 | 1 | 2;
      const currentSlider = currentConfig.sliders[sliderIndex];
      const updatedSlider = {
        midi_type: config.midi_type ?? currentSlider.midi_type,
        channel: config.channel ?? currentSlider.channel,
        cc: config.cc ?? currentSlider.cc,
        min: config.min ?? currentSlider.min,
        max: config.max ?? currentSlider.max,
        invert: config.invert ?? currentSlider.invert,
        spring_center: config.spring_center ?? currentSlider.spring_center,
      };

      const newSliders: [typeof updatedSlider, typeof updatedSlider, typeof updatedSlider] = [
        sliderIndex === 0 ? updatedSlider : currentConfig.sliders[0],
        sliderIndex === 1 ? updatedSlider : currentConfig.sliders[1],
        sliderIndex === 2 ? updatedSlider : currentConfig.sliders[2],
      ];

      return {
        analogConfig: {
          ...currentConfig,
          sliders: newSliders,
        },
      };
    }),

  updatePedalConfig: (config) =>
    set((state) => {
      // Create default analog config if not present
      const currentConfig: AnalogConfig = state.analogConfig ?? {
        sliders: [
          { midi_type: 'cc', channel: 0, cc: 1, min: 0, max: 127, invert: false, spring_center: false },
          { midi_type: 'cc', channel: 0, cc: 2, min: 0, max: 127, invert: false, spring_center: false },
          { midi_type: 'cc', channel: 0, cc: 3, min: 0, max: 127, invert: false, spring_center: false },
        ],
        pedal: {
          detect_mode: 'auto',
          sustain: { cc: 64, channel: 0, invert: false },
          expression: { cc: 11, channel: 0, min: 0, max: 127, invert: false },
        },
      };

      // Build new pedal config, handling nested sustain/expression updates
      const newPedal = {
        detect_mode: config.detect_mode ?? currentConfig.pedal.detect_mode,
        sustain: config.sustain ?? currentConfig.pedal.sustain,
        expression: config.expression ?? currentConfig.pedal.expression,
      };

      return {
        analogConfig: {
          ...currentConfig,
          pedal: newPedal,
        },
      };
    }),

  // Power budget setters
  setConverterAmps: (amps) =>
    set((state) => ({
      powerBudget: {
        ...state.powerBudget,
        converterAmps: Math.max(0.5, Math.min(20, amps)),
      },
    })),

  setMaxSimultaneousPressed: (count) =>
    set((state) => ({
      powerBudget: {
        ...state.powerBudget,
        maxSimultaneousPressed: Math.max(1, Math.min(192, Math.round(count))),
      },
    })),

  // Live visualization toggle
  setLiveVisualizationEnabled: (enabled) => {
    try {
      localStorage.setItem('stak-live-viz', enabled ? '1' : '0');
    } catch { /* localStorage unavailable */ }
    set({ liveVisualizationEnabled: enabled });
  },

  // Refresh action - increments trigger to force color recalculation
  refreshColors: () =>
    set((state) => ({
      refreshTrigger: state.refreshTrigger + 1,
    })),

  // Load configuration - replaces current state with imported config
  loadConfiguration: (config) =>
    set((state) => ({
      ...state,
      ...config,
      // Increment refresh trigger to force color updates
      refreshTrigger: state.refreshTrigger + 1,
    })),
}));

export const selectActiveTuning = (state: ConfigStore): TuningTable | null =>
  state.tuningEnabled && state.tuningTable ? state.tuningTable : null;

export type MappingMode = 'note_mapping' | 'microtonal';

export const selectMappingMode = (state: ConfigStore): MappingMode =>
  state.tuningEnabled && state.tuningTable ? 'microtonal' : 'note_mapping';
